import React, { useState } from "react"
import { graphql } from "gatsby"
import Layout from "../../../src/components/modules/Layout"

import ProductHeader from "../../../src/components/blocks/ProductHeader"

import Seo from "@yumgmbh/gatsby-theme-yum-components/src/components/atoms/Seo"

import { useTranslation } from "react-i18next"
import striptags from "striptags"
import ProductHeaderBreadcrumbs from "../../../src/components/blocks/ProductHeaderBreadcrumbs"
import ProductOverview from "../../../src/components/blocks/ProductOverview"
import { mergeAndSortVariantsWithProducts } from "../../../src/utilities/pim-data-utils"
import RelatedVariantsCardsSlider from "../../../src/components/blocks/RelatedVariantsCardsSlider"
import MultiVariantProductOverview from "../../../src/components/blocks/MultiVariantProductOverview"
import ProductServiceInfoBlock from "../../../src/components/blocks/ProductServiceInfoBlock"

const SingleProductMultiVariantTemplate = ({ pageContext, data }) => {
  const entry = data.entry
  const primaryVariant = data.primaryVariant
  const area = data.area
  const group = data.group
  const variants = data.variants.edges

  const {
    crossSellingVariantIDs: crossSellingVariantIDsOrder,
    accessoriesVariantIDs: accessoriesVariantIDsOrder,
    componentsVariantIDs: componentsVariantIDsOrder,
  } = pageContext

  const crossSellingVariants = mergeAndSortVariantsWithProducts(
    crossSellingVariantIDsOrder,
    data.crossSellingVariants,
    data.crossSellingProducts
  )

  const accessoriesVariants = mergeAndSortVariantsWithProducts(
    accessoriesVariantIDsOrder,
    data.accessoriesVariants,
    data.accessoriesProducts
  )

  const componentsVariants = mergeAndSortVariantsWithProducts(
    componentsVariantIDsOrder,
    data.componentsVariants,
    data.componentsProducts
  )

  let breadcrumbs = [
    { link: entry.rootPage.fullSlug, link_text: entry.rootPage.name },
    {
      link: area?.fullSlug,
      link_text: area?.name,
    },
    {
      link: group?.fullSlug,
      link_text: group?.name,
    },
  ]

  return (
    <Layout language={entry.language}>
      <Seo
        title={`${entry.displayName?.value} | ${group.name}`}
        description={`${striptags(
          entry.productDescription?.value?.replace(
            /<\/li><li>/g,
            "</li> | <li>"
          )
        )}`}
        og_image={entry.properties?.mainImage[0]}
        language={entry.language}
      />
      <ProductHeaderBreadcrumbs breadcrumbs={breadcrumbs} _uid={entry?.ID} />
      <MultiVariantProductOverview
        product={entry}
        primaryVariant={primaryVariant}
        variants={variants}
      />
      {componentsVariants.length > 0 && (
        <RelatedVariantsCardsSlider
          items={componentsVariants}
          headline_i18n_key="generic.tabs.bundle_components_title"
          _attrId={primaryVariant.componentsVariantIDs.originId}
        />
      )}
      {(crossSellingVariants.length > 0 || accessoriesVariants.length > 0) && (
        <RelatedVariantsCardsSlider
          items={[...crossSellingVariants, ...accessoriesVariants]}
          headline_i18n_key="generic.tabs.cross_selling_title"
          _attrId={[
            primaryVariant.crossSellingVariantIDs?.originId,
            primaryVariant.accessoriesVariantIDs?.originId,
          ]
            .filter((e) => !!e) // remove nulls
            .join(",")}
        />
      )}
      <ProductServiceInfoBlock />
    </Layout>
  )
}

export const query = graphql`
  query VariantQuery(
    $primaryVariantId: String
    $productId: String!
    $locale: String
    $groupId: String
    $areaId: String
    $crossSellingVariantIDs: [String]
    $crossSellingProductIDs: [String]
    $accessoriesVariantIDs: [String]
    $accessoriesProductIDs: [String]
    $componentsVariantIDs: [String]
    $componentsProductIDs: [String]
  ) {
    entry: contentServProduct(ID: { eq: $productId }, locale: { eq: $locale }) {
      ID
      fullSlug
      language
      displayName {
        value
        originId
      }
      productDescription {
        value
        originId
      }
      mainImage {
        originId
        items {
          label
          value
        }
      }
      rootPage {
        name
        fullSlug
      }
      isNewToggle {
        value
      }
      isNewFromDate {
        value
      }
      isNewUntilDate {
        value
      }
    }
    primaryVariant: contentServVariant(
      ID: { eq: $primaryVariantId }
      locale: { eq: $locale }
    ) {
      name
      displayName {
        value
        originId
      }
      externalKey
      ID
      language
      fullSlug
      variantContainerType {
        value
      }
      rootPage {
        name
        fullSlug
      }
      dimensionText {
        originId
        value
      }
      deliveryScope {
        originId
        value
      }
      longDescriptionHtml {
        originId
        value
      }
      productBenefitsHtml {
        label
        originId
        value
      }
      pictogramImages {
        originId
        items {
          label
          value
        }
      }
      productImages {
        originId
        items {
          label
          value
        }
      }
      productVideos {
        originId
        items {
          label
          value
        }
      }
      applicationImages {
        originId
        items {
          label
          value
        }
      }
      detailImages {
        originId
        items {
          label
          value
        }
      }
      marketingImages {
        originId
        items {
          label
          value
        }
      }
      packagingImages {
        originId
        items {
          label
          value
        }
      }
      miscImages {
        originId
        items {
          label
          value
        }
      }
      changeOverLabel {
        resolvedOriginId
        originId
        label
        value
      }
      crossSellingVariantIDs {
        originId
      }
      accessoriesVariantIDs {
        originId
      }
      componentsVariantIDs {
        originId
      }
    }
    group: contentServProductGroup(
      ID: { eq: $groupId }
      locale: { eq: $locale }
    ) {
      name
      fullSlug
    }
    area: contentServProductArea(ID: { eq: $areaId }, locale: { eq: $locale }) {
      name
      fullSlug
    }
    variants: allContentServVariant(
      filter: {
        hierarchy: { product: { id: { eq: $productId } } }
        locale: { eq: $locale }
      }
      sort: { fields: treeSorting, order: ASC }
    ) {
      edges {
        node {
          ID
          fullSlug
          externalKey
          variantContainerType {
            value
          }
          dimensionText {
            originId
            value
          }
          longDescriptionHtml {
            originId
            value
          }
          productBenefitsHtml {
            label
            originId
            value
          }
          articleNumber {
            originId
            label
            value
          }
          sapArticleNumber {
            originId
            label
            value
          }
          changeOverLabel {
            originId
            label
            value
          }
          technicalData {
            originId
            label
            value
          }
          dimensionalData {
            originId
            label
            value
          }
          commercialData {
            label
            value
            originId
          }
          mainApplicationArea {
            label
            values
            originId
          }
          sideApplicationArea {
            label
            values
            originId
          }
          productImages {
            originId
            items {
              label
              value
            }
          }
          productVideos {
            originId
            items {
              label
              value
            }
          }
          applicationImages {
            originId
            items {
              label
              value
            }
          }
          detailImages {
            originId
            items {
              label
              value
            }
          }
          marketingImages {
            originId
            items {
              label
              value
            }
          }
          packagingImages {
            originId
            items {
              label
              value
            }
          }
          miscImages {
            originId
            items {
              label
              value
            }
          }
          documentationFiles {
            originId
            items {
              label
              value
            }
          }
        }
      }
    }
    crossSellingVariants: allContentServVariant(
      filter: { ID: { in: $crossSellingVariantIDs }, locale: { eq: $locale } }
    ) {
      edges {
        node {
          ID
          displayName {
            value
            originId
          }
          fullSlug
          hierarchy {
            product {
              id
            }
          }
          productImages {
            originId
            items {
              label
              value
            }
          }
        }
      }
    }
    crossSellingProducts: allContentServProduct(
      filter: { ID: { in: $crossSellingProductIDs }, locale: { eq: $locale } }
    ) {
      edges {
        node {
          ID
          isNewToggle {
            value
          }
          isNewFromDate {
            value
          }
          isNewUntilDate {
            value
          }
        }
      }
    }
    accessoriesVariants: allContentServVariant(
      filter: { ID: { in: $accessoriesVariantIDs }, locale: { eq: $locale } }
    ) {
      edges {
        node {
          ID
          displayName {
            value
            originId
          }
          fullSlug
          hierarchy {
            product {
              id
            }
          }
          productImages {
            originId
            items {
              label
              value
            }
          }
        }
      }
    }
    accessoriesProducts: allContentServProduct(
      filter: { ID: { in: $accessoriesProductIDs }, locale: { eq: $locale } }
    ) {
      edges {
        node {
          ID
          isNewToggle {
            value
          }
          isNewFromDate {
            value
          }
          isNewUntilDate {
            value
          }
        }
      }
    }
    componentsVariants: allContentServVariant(
      filter: { ID: { in: $componentsVariantIDs }, locale: { eq: $locale } }
    ) {
      edges {
        node {
          ID
          displayName {
            value
            originId
          }
          changeOverLabel {
            value
            originId
          }
          fullSlug
          hierarchy {
            product {
              id
            }
          }
          productImages {
            originId
            items {
              label
              value
            }
          }
        }
      }
    }
    componentsProducts: allContentServProduct(
      filter: { ID: { in: $componentsProductIDs }, locale: { eq: $locale } }
    ) {
      edges {
        node {
          ID
          isNewToggle {
            value
          }
          isNewFromDate {
            value
          }
          isNewUntilDate {
            value
          }
        }
      }
    }
  }
`

export default SingleProductMultiVariantTemplate
